import React, { useMemo, useState } from 'react'
import propTypes from 'prop-types'
import {
	Collapse,
	IconButton,
	InputAdornment,
	List,
	ListItem,
	ListItemText,
	TextField,
	Typography,
	makeStyles
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Close'
import Skeleton from '@material-ui/lab/Skeleton'

const useStyles = makeStyles((theme) => ({
	root: {
		width: '100%',
		paddingTop: theme.spacing(1)
	},
	header: {
		padding: theme.spacing(1, 2, 0.5, 2),
		color: theme.palette.text.secondary,
		letterSpacing: 1.2,
		textTransform: 'uppercase'
	},
	row: {
		paddingTop: theme.spacing(0.5),
		paddingBottom: theme.spacing(0.5),
		borderRadius: theme.shape.borderRadius,
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1)
	},
	parentRow: {
		paddingLeft: theme.spacing(0.5)
	},
	// Children sit visually under the parent's label: parent text starts at
	// roughly chevron-width past the row's left edge (~36px); children indent
	// to spacing(7) (~56px) so their label is clearly nested past the parent.
	childRow: {
		paddingLeft: theme.spacing(7)
	},
	selected: {
		backgroundColor:
			theme.palette.action && theme.palette.action.selected
				? theme.palette.action.selected
				: 'rgba(0, 0, 0, 0.08)'
	},
	labelText: {
		fontWeight: 500
	},
	selectedLabelText: {
		fontWeight: 600,
		color:
			theme.palette.colors && theme.palette.colors.purple
				? theme.palette.colors.purple
				: theme.palette.primary.main
	},
	count: {
		color: theme.palette.text.secondary,
		marginLeft: theme.spacing(1)
	},
	skeletonRow: {
		margin: theme.spacing(0.5, 2)
	},
	chevron: {
		padding: theme.spacing(0.25),
		marginRight: theme.spacing(0.5)
	},
	chevronSpacer: {
		display: 'inline-block',
		width: theme.spacing(4)
	},
	searchWrapper: {
		padding: theme.spacing(0, 2, 1, 2)
	},
	searchInput: {
		fontSize: '0.875rem'
	},
	emptyMatch: {
		padding: theme.spacing(1, 2),
		color: theme.palette.text.secondary,
		fontStyle: 'italic'
	}
}))

const CategorySidebar = (props) => {
	const classes = useStyles()
	const { categoryCounts, allCount, selectedCategoryId, onSelectCategory, isLoading } = props

	const parents = (categoryCounts || []).filter((c) => c.ParentId == null)
	const childrenByParent = (parentId) =>
		(categoryCounts || []).filter((c) => c.ParentId === parentId)

	// Track which parents are collapsed (default: all expanded).
	const [collapsedIds, setCollapsedIds] = useState(() => new Set())
	const toggleCollapsed = (parentId) => {
		setCollapsedIds((prev) => {
			const next = new Set(prev)
			if (next.has(parentId)) next.delete(parentId)
			else next.add(parentId)
			return next
		})
	}

	const [searchTerm, setSearchTerm] = useState('')
	const normalizedSearch = searchTerm.trim().toLowerCase()
	const isSearching = normalizedSearch.length > 0

	// Filter: a parent is visible when it matches OR any child matches; a child
	// is visible when it matches OR its parent matches. Memoized per term/data.
	const visibleParents = useMemo(() => {
		if (!isSearching) return parents
		return parents.filter((p) => {
			if ((p.Name || '').toLowerCase().includes(normalizedSearch)) return true
			return childrenByParent(p.CategoryId).some((c) => (c.Name || '').toLowerCase().includes(normalizedSearch))
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [parents, categoryCounts, isSearching, normalizedSearch])

	const visibleChildren = (parent) => {
		const all = childrenByParent(parent.CategoryId)
		if (!isSearching) return all
		const parentMatches = (parent.Name || '').toLowerCase().includes(normalizedSearch)
		if (parentMatches) return all
		return all.filter((c) => (c.Name || '').toLowerCase().includes(normalizedSearch))
	}

	const showSkeleton = isLoading && (!categoryCounts || categoryCounts.length === 0)
	const showEmptyMatch = isSearching && !showSkeleton && visibleParents.length === 0

	return (
		<div className={classes.root}>
			<Typography variant='overline' className={classes.header}>Categories</Typography>
			<div className={classes.searchWrapper}>
				<TextField
					fullWidth
					size='small'
					variant='outlined'
					placeholder='Search categories...'
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					InputProps={{
						className: classes.searchInput,
						startAdornment: (
							<InputAdornment position='start'>
								<SearchIcon fontSize='small' />
							</InputAdornment>
						),
						endAdornment: searchTerm ? (
							<InputAdornment position='end'>
								<IconButton size='small' onClick={() => setSearchTerm('')} aria-label='clear category search'>
									<ClearIcon fontSize='small' />
								</IconButton>
							</InputAdornment>
						) : null
					}}
				/>
			</div>
			<List dense disablePadding>
				{!isSearching && (
					<SidebarRow
						label='All'
						count={allCount || 0}
						selected={selectedCategoryId == null}
						onClick={() => onSelectCategory(null)}
						classes={classes}
					/>
				)}
				{showSkeleton && <SkeletonRows classes={classes} />}
				{showEmptyMatch && (
					<Typography variant='caption' className={classes.emptyMatch}>
						No categories match "{searchTerm}".
					</Typography>
				)}
				{!showSkeleton &&
					visibleParents.map((parent) => {
						const children = visibleChildren(parent)
						const hasChildren = children.length > 0
						// While searching, force-expand every shown parent so matching
						// children are visible regardless of the user's collapse state.
						const isCollapsed = isSearching ? false : collapsedIds.has(parent.CategoryId)
						return (
							<React.Fragment key={parent.CategoryId}>
								<SidebarRow
									label={parent.Name}
									count={parent.Count}
									selected={selectedCategoryId === parent.CategoryId}
									onClick={() => onSelectCategory(parent.CategoryId)}
									isParent
									hasChildren={hasChildren}
									isCollapsed={isCollapsed}
									onToggleCollapsed={isSearching ? null : () => toggleCollapsed(parent.CategoryId)}
									classes={classes}
								/>
								{hasChildren && (
									<Collapse in={!isCollapsed} timeout='auto' unmountOnExit>
										{children.map((child) => (
											<SidebarRow
												key={child.CategoryId}
												label={child.Name}
												count={child.Count}
												selected={selectedCategoryId === child.CategoryId}
												onClick={() => onSelectCategory(child.CategoryId)}
												isChild
												classes={classes}
											/>
										))}
									</Collapse>
								)}
							</React.Fragment>
						)
					})}
			</List>
		</div>
	)
}

const SidebarRow = ({
	label,
	count,
	selected,
	onClick,
	isChild,
	isParent,
	hasChildren,
	isCollapsed,
	onToggleCollapsed,
	classes
}) => {
	const handleChevronClick = (e) => {
		e.stopPropagation()
		if (onToggleCollapsed) onToggleCollapsed()
	}
	return (
		<ListItem
			button
			onClick={onClick}
			className={`${classes.row}${isParent ? ' ' + classes.parentRow : ''}${isChild ? ' ' + classes.childRow : ''}${selected ? ' ' + classes.selected : ''}`}
		>
			{isParent && (
				hasChildren ? (
					<IconButton
						size='small'
						className={classes.chevron}
						onClick={handleChevronClick}
						aria-label={isCollapsed ? 'expand' : 'collapse'}
					>
						{isCollapsed ? <ChevronRightIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
					</IconButton>
				) : (
					<span className={classes.chevronSpacer} />
				)
			)}
			<ListItemText
				disableTypography
				primary={
					<>
						<Typography
							component='span'
							variant='body2'
							className={selected ? classes.selectedLabelText : classes.labelText}
						>
							{label}
						</Typography>
						<Typography component='span' variant='caption' className={classes.count}>
							({count})
						</Typography>
					</>
				}
			/>
		</ListItem>
	)
}

const SkeletonRows = ({ classes }) => (
	<>
		{[0, 1, 2, 3].map((i) => (
			<Skeleton key={i} variant='text' width='80%' className={classes.skeletonRow} />
		))}
	</>
)

CategorySidebar.propTypes = {
	categoryCounts: propTypes.arrayOf(
		propTypes.shape({
			CategoryId: propTypes.number.isRequired,
			Name: propTypes.string,
			ParentId: propTypes.number,
			Count: propTypes.number
		})
	),
	allCount: propTypes.number,
	selectedCategoryId: propTypes.number,
	onSelectCategory: propTypes.func.isRequired,
	isLoading: propTypes.bool
}

CategorySidebar.defaultProps = {
	categoryCounts: [],
	allCount: 0,
	selectedCategoryId: null,
	isLoading: false
}

export default CategorySidebar
